import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  OrganizationSchema,
  SupplierCreditNoteListItemSchema,
  SupplierCreditNoteSchema,
  SupplierInvoiceDetailSchema,
  SupplierInvoiceSchema,
} from '@taqon/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { getData, getPage, newIdempotencyKey, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { refreshAfterPosting } from './cache';
import { RecordLink } from './detail';
import {
  businessToday,
  emptyLine,
  lineProblems,
  lineRequests,
  LinesEditor,
  linesComplete,
  type LineDraft,
} from './line-editor';
import { RefusalState } from './refusal';

/**
 * Supplier credit notes (FRD-FIN-075, FRD-VAT-037): the list, with the invoice each credits. A
 * credit note is always against a posted invoice and reduces its payable; it is a new document,
 * never an edit of the invoice. New ones start here or from the invoice's own page.
 */
export function CreditNotesPage() {
  const session = useSession();
  const queryClient = useQueryClient();
  const canCreate = hasCapability(session.data, 'finance.supplier_credit_note:create');
  const canPost = hasCapability(session.data, 'finance.supplier_credit_note:post');
  const notes = useQuery({
    queryKey: ['supplier-credit-notes'],
    queryFn: () =>
      getPage(
        '/api/v1/finance/supplier-credit-notes?pageSize=50&sort=-document_no',
        SupplierCreditNoteListItemSchema,
      ),
  });
  const post = useMutation({
    mutationFn: (note: { id: string; version: number }) =>
      postJson(
        `/api/v1/finance/supplier-credit-notes/${note.id}/post`,
        {},
        SupplierCreditNoteSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: note.version },
      ),
    onSuccess: () => refreshAfterPosting(queryClient),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Supplier credit notes"
        description="Credit notes against posted supplier invoices. Posting reduces the payable and reverses the input VAT."
        actions={
          canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/supplier-credit-notes/new"
            >
              New credit note
            </Button>
          )
        }
      />
      {post.isError && <RefusalState error={post.error} />}
      {notes.isError && <ErrorState message={notes.error.message} />}
      <Section title="Credit notes">
        {notes.isPending && <LoadingState label="Loading credit notes" />}
        {notes.data?.items.length === 0 && <EmptyState title="No credit notes yet" />}
        {notes.data !== undefined && notes.data.items.length > 0 && (
          <Table size="small" aria-label="Supplier credit notes">
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Supplier reference</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Net</TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell>Status</TableCell>
                {canPost && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {notes.data.items.map((note) => (
                <TableRow key={note.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <RecordLink to={`/supplier-credit-notes/${note.id}`}>
                      {note.documentNo}
                    </RecordLink>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <RecordLink to={`/supplier-invoices/${note.supplierInvoiceId}`}>
                      {note.supplierInvoiceDocumentNo}
                    </RecordLink>
                  </TableCell>
                  <TableCell>{note.supplierCreditNoteReference}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{note.creditNoteDate}</TableCell>
                  <TableCell align="right">{note.netTotal}</TableCell>
                  <TableCell align="right">{note.vatTotal}</TableCell>
                  <TableCell align="right">{note.grossTotal}</TableCell>
                  <TableCell>
                    <StatusChip status={note.status} />
                  </TableCell>
                  {canPost && (
                    <TableCell align="right">
                      {note.status === 'draft' && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={post.isPending}
                          onClick={() => {
                            post.mutate({ id: note.id, version: note.version });
                          }}
                        >
                          Post
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}

/**
 * A new credit note (`/supplier-credit-notes/new`, or `?invoice=<id>` from an invoice's page):
 * against a posted invoice with a payable outstanding, with its own lines, accounts and
 * dimensions. VAT is determined by the API as for the invoice (FRD-VAT-037); a reverse-charge tax
 * code is refused, as it is everywhere until that treatment exists.
 */
export function NewCreditNotePage() {
  const session = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canCreate = hasCapability(session.data, 'finance.supplier_credit_note:create');
  const [invoiceId, setInvoiceId] = useState(params.get('invoice') ?? '');
  const [reference, setReference] = useState('');
  const [creditDate, setCreditDate] = useState(() => businessToday(session.data?.businessTimezone));
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine(1)]);

  const organization = useQuery({
    queryKey: ['organization'],
    queryFn: () => getData('/api/v1/organization/organization', OrganizationSchema),
  });
  const open = useQuery({
    queryKey: ['supplier-invoices', 'open', 'all'],
    enabled: canCreate,
    queryFn: () =>
      getPage(
        '/api/v1/finance/supplier-invoices?pageSize=200&sort=-document_no' +
          '&filter[status]=posted&filter[open]=true',
        SupplierInvoiceSchema,
      ),
  });
  const invoice = useQuery({
    queryKey: ['supplier-invoice', invoiceId],
    enabled: invoiceId !== '',
    queryFn: () =>
      getData(`/api/v1/finance/supplier-invoices/${invoiceId}`, SupplierInvoiceDetailSchema),
  });
  // The chosen invoice stays selectable even beyond the first page of open invoices.
  const options = [...(open.data?.items ?? [])];
  if (invoice.data !== undefined && !options.some((o) => o.id === invoice.data.invoice.id)) {
    options.unshift(invoice.data.invoice);
  }
  const target = invoice.data?.invoice;
  const creditable = target?.status === 'posted';

  const problems = [
    ...(target !== undefined && !creditable
      ? [`Invoice ${target.documentNo} is not posted: only a posted invoice can be credited.`]
      : []),
    ...lineProblems(lines),
  ];
  const complete =
    organization.data !== undefined &&
    creditable &&
    reference.trim() !== '' &&
    creditDate !== '' &&
    reason.trim() !== '' &&
    linesComplete(lines);

  const create = useMutation({
    mutationFn: () =>
      postJson(
        '/api/v1/finance/supplier-credit-notes',
        {
          supplierInvoiceId: invoiceId,
          supplierCreditNoteReference: reference.trim(),
          creditNoteDate: creditDate,
          taxDate: creditDate,
          reason: reason.trim(),
          organizationId: organization.data?.id ?? '',
          lines: lineRequests(lines),
        },
        SupplierCreditNoteSchema,
        session.data?.csrfToken ?? '',
        // One key per deliberate submission: a repeat returns the original document.
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: async (created) => {
      await refreshAfterPosting(queryClient);
      await navigate(`/supplier-credit-notes/${created.id}`);
    },
  });

  const back = (
    <Button
      component={RouterLink}
      to={invoiceId === '' ? '/supplier-credit-notes' : `/supplier-invoices/${invoiceId}`}
      startIcon={<ArrowBackIcon />}
    >
      {invoiceId === '' ? 'All credit notes' : 'Back to the invoice'}
    </Button>
  );
  if (!canCreate) {
    return (
      <Stack spacing={3}>
        <PageHeader title="New supplier credit note" actions={back} />
        <Section>
          <EmptyState
            title="Not permitted"
            detail="Creating a supplier credit note needs its create permission."
          />
        </Section>
      </Stack>
    );
  }
  return (
    <Stack spacing={3}>
      <PageHeader
        title="New supplier credit note"
        description="Captured as a draft against a posted invoice; posting reduces its payable."
        actions={back}
      />
      <Section title="Credit note">
        <Stack
          spacing={2.5}
          sx={{ p: 2.5 }}
          component="form"
          aria-label="New supplier credit note"
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            if (complete && problems.length === 0 && !create.isPending) create.mutate();
          }}
        >
          <TextField
            select
            size="small"
            label="Invoice credited"
            required
            value={options.some((o) => o.id === invoiceId) ? invoiceId : ''}
            helperText={
              target === undefined
                ? 'A posted invoice with a payable outstanding'
                : `${invoice.data?.supplier.summary ?? 'Supplier not visible'} — outstanding ${target.outstandingAmount}`
            }
            onChange={(e) => {
              setInvoiceId(e.target.value);
              // Lines that credited the previous invoice's lines cannot credit this one's.
              setLines((current) =>
                current.map((line) => ({ ...line, supplierInvoiceLineId: '' })),
              );
            }}
          >
            {options.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.documentNo} — {o.supplierInvoiceNumber} — outstanding {o.outstandingAmount}
              </MenuItem>
            ))}
          </TextField>
          {invoice.isError && <ErrorState message={invoice.error.message} />}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              size="small"
              label="Supplier's credit note reference"
              required
              value={reference}
              sx={{ flexGrow: 1 }}
              onChange={(e) => {
                setReference(e.target.value);
              }}
            />
            <TextField
              size="small"
              type="date"
              label="Credit note date"
              required
              value={creditDate}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                setCreditDate(e.target.value);
              }}
            />
          </Stack>
          <TextField
            size="small"
            label="Reason"
            required
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
          <LinesEditor
            lines={lines}
            onChange={setLines}
            creditableLines={creditable ? invoice.data?.lines : undefined}
          />
          {problems.length > 0 && (
            <Alert severity="warning" variant="outlined">
              {problems.map((p) => (
                <div key={p}>{p}</div>
              ))}
            </Alert>
          )}
          {create.isError && <RefusalState error={create.error} />}
          <Box>
            <Button
              type="submit"
              variant="contained"
              disabled={!complete || problems.length > 0 || create.isPending}
            >
              {create.isPending ? 'Capturing…' : 'Capture credit note'}
            </Button>
          </Box>
        </Stack>
      </Section>
    </Stack>
  );
}
